import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { AppShell } from '@/components/AppShell'
import { ErrorNote, Loading } from '@/components/Feedback'
import { AddEquipment } from '@/routes/AddEquipment'
import { Dashboard } from '@/routes/Dashboard'
import { Fixtures } from '@/routes/Fixtures'
import { ForgotPassword } from '@/routes/ForgotPassword'
import { Inventory } from '@/routes/Inventory'
import { Locations } from '@/routes/Locations'
import { LocationTrash } from '@/routes/LocationTrash'
import { Privacy } from '@/routes/Privacy'
import { Profile } from '@/routes/Profile'
import { QuickAddLighting } from '@/routes/QuickAddLighting'
import { ResetPassword } from '@/routes/ResetPassword'
import { SignIn } from '@/routes/SignIn'
import { Terms } from '@/routes/Terms'
import { Trash } from '@/routes/Trash'
import { Users } from '@/routes/Users'
import { parseInventorySearch } from '@/lib/inventorySearch'
import { useSessionStore } from '@/lib/session'

const rootRoute = createRootRoute({
  component: Outlet,
  // A render-time exception in any route falls back to the branded error note
  // rather than TanStack's bare default, so a broken screen still looks like
  // the app. `redirect()` is a controlled throw the router handles itself, so
  // the auth guards don't land here.
  errorComponent: ({ error }) => <ErrorNote error={error} />,
  notFoundComponent: () => <ErrorNote error={new Error('Page not found.')} />,
})

/**
 * Everything inside the shell needs a session. `beforeLoad` bounces signed-out
 * visitors to the sign-in screen, which lives outside the shell.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: AppShell,
  beforeLoad: () => {
    if (useSessionStore.getState().token === null) {
      throw redirect({ to: '/sign-in' })
    }
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: Dashboard,
})

/** The filters ride in the query string so a filtered list is linkable. */
const inventoryRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory',
  component: Inventory,
  validateSearch: parseInventorySearch,
})

// Declared before the dynamic sibling so "new" and "trash" are never read as
// item ids.
const addEquipmentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory/new',
  component: AddEquipment,
})

const quickAddLightingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory/quick-add-lighting',
  component: QuickAddLighting,
})

const trashRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory/trash',
  component: Trash,
})

const equipmentDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory/$itemId',
  component: lazyRouteComponent(() => import('@/routes/EquipmentDetail'), 'EquipmentDetail'),
})

const fixturesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/fixtures',
  component: Fixtures,
})

const fixtureDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/fixtures/$fixtureModelId',
  component: lazyRouteComponent(() => import('@/routes/FixtureDetail'), 'FixtureDetail'),
})

const locationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/locations',
  component: Locations,
})

// Declared before the dynamic sibling so "trash" is never read as a location id.
const locationTrashRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/locations/trash',
  component: LocationTrash,
})

const locationDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/locations/$locationId',
  component: lazyRouteComponent(() => import('@/routes/LocationDetail'), 'LocationDetail'),
})

const stocktakeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/stocktake',
  component: lazyRouteComponent(() => import('@/routes/Stocktake'), 'Stocktake'),
})

/** Everyone's own account, admin or crew alike (issue #14). */
const profileRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/profile',
  component: Profile,
})

const usersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/users',
  component: Users,
})

const userEditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/users/$userId',
  component: lazyRouteComponent(() => import('@/routes/UserEdit'), 'UserEdit'),
})

/** Sign-in sits outside the shell — no nav bar, no session required. */
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignIn,
  // A Google sign-in that failed comes back here as a redirect carrying the
  // reason (issue #13); the screen turns the slug into a sentence.
  validateSearch: (search: Record<string, unknown>): { authError?: string } =>
    typeof search.authError === 'string' ? { authError: search.authError } : {},
  beforeLoad: () => {
    if (useSessionStore.getState().token !== null) {
      throw redirect({ to: '/' })
    }
  },
})

/**
 * Terms and privacy are public — linked from the sign-in screen, and from
 * Google's consent screen, which is seen by people who are not signed in and
 * may never be.
 */
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: Terms,
})

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: Privacy,
})

/**
 * Forgot/reset password sit outside the shell too, the same way sign-in
 * does — reachable while signed out, no nav bar.
 */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPassword,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
})

const routeTree = rootRoute.addChildren([
  shellRoute.addChildren([
    dashboardRoute,
    addEquipmentRoute,
    quickAddLightingRoute,
    trashRoute,
    equipmentDetailRoute,
    inventoryRoute,
    fixturesRoute,
    fixtureDetailRoute,
    locationsRoute,
    locationTrashRoute,
    locationDetailRoute,
    stocktakeRoute,
    profileRoute,
    usersRoute,
    userEditRoute,
  ]),
  signInRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  termsRoute,
  privacyRoute,
])

export const router = createRouter({
  routeTree,
  // Shown while a lazy route's chunk loads. The drill-down screens
  // (item/fixture/location detail, stocktake, user edit) are code-split, so a
  // first visit to one briefly suspends; this keeps that moment from flashing
  // blank.
  defaultPendingComponent: () => <Loading />,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
